import { Component, computed, effect, Inject, inject, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterModule } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, PopupRequest } from '@azure/msal-browser';
import { UserStateService } from 'libs/core/state/user-state.service';
import { SpinnerComponent } from 'libs/shared';
import { LoadingStateService, PosthogService, SchemaMembershipStateService } from '@net3-multiplatform-client/libs';
import posthog from "posthog-js";

@Component({
  imports: [RouterModule, SpinnerComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly _destroying$ = new Subject<void>();

  private userStateService = inject(UserStateService);
  private loadingService = inject(LoadingStateService);
  private schemaMembershipService = inject(SchemaMembershipStateService);
  
  user = computed(() => this.userStateService.user());

  schema = computed ( () => this.schemaMembershipService.schema());

  loading = this.loadingService.showLoadingPage;

  constructor(
    private authMsalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private posthogService: PosthogService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) { 
    this.router.events
      .pipe(filter(e =>
        e instanceof NavigationEnd
      ))
      .subscribe(e => {
        this.loading.set(false);
      });

      //Detectem quan s'inicialitza un usuari per poder executar l'identificació de PostHog.
      effect(() => {
        const user = this.user();
        if (user) {
          posthog.identify(user.email,user);
        }
      });
      //Detectem quan s'inicialitza el schema per l'identificació de tenant (group) de PostHog.
      effect(() => {
        const schema = this.schema();
        if (schema) {
          posthog.group('tenant', schema.name);
          posthog.register({  
            current_tenant_id: schema.schema_id,  
            current_tenant_role: schema.role
          });  
        }
      });
  }

  ngOnInit(): void {
    this.authMsalService.handleRedirectObservable().subscribe();

    this.authMsalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    //Capturem logout de msal per executar logica interna, eliminar token de storage.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_REMOVED ||  msg.eventType === EventType.LOGOUT_SUCCESS ||
      msg.eventType === EventType.LOGOUT_END),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {        
        localStorage.removeItem("token");
        this.userStateService.clearUserInfo();
        //PostHog: Netejem properties al fer signout.
        posthog.unregister('current_tenant_id');  
        posthog.unregister('current_tenant_role');
        posthog.resetGroups();
        this.router.navigateByUrl('/signin', { replaceUrl: true });
      });

    // Sincronitza l'activeAccount amb el compte del login/acquisició més recent.
    // Sense això, després d'un canvi d'usuari, l'activeAccount pot continuar apuntant a l'anterior.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        if (payload?.account) {
          this.authMsalService.instance.setActiveAccount(payload.account);
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      })
  }

  checkAndSetActiveAccount(){
    const activeAccount = this.authMsalService.instance.getActiveAccount();
    if (activeAccount) return;

    // Amb 1 compte cacheat la selecció és inequívoca; amb 0 o >1 forcem login explícit
    // per evitar operar com un usuari equivocat.
    const accounts = this.authMsalService.instance.getAllAccounts();
    if (accounts.length === 1) {
      this.authMsalService.instance.setActiveAccount(accounts[0]);
    } else {
      this.router.navigateByUrl('/signin', { replaceUrl: true });
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest){
      this.authMsalService.loginPopup({...this.msalGuardConfig.authRequest} as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authMsalService.instance.setActiveAccount(response.account);
        });
      } else {
        this.authMsalService.loginPopup()
          .subscribe((response: AuthenticationResult) => {
            this.authMsalService.instance.setActiveAccount(response.account);
      });
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authMsalService.logoutPopup({
        mainWindowRedirectUri: "/signin"
      });
    } else {
      this.authMsalService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
